import { Page } from "@/components/Page";
import { PageHero } from "@/components/PageHero";
import { ContactForm } from "@/components/ContactForm";
import { HoursSection } from "@/components/HoursSection";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Section";
import { Phone, MessageCircle, MapPin, Clock } from "lucide-react";
import { CLINIC, telLink, whatsappLink, STOCK } from "@/data/site";

const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(CLINIC.mapsQuery)}&output=embed`;

export default function Contact() {
  return (
    <Page>
      <PageHero
        crumb="Contact"
        overline="Get In Touch"
        title={"Book Your\nAppointment"}
        subtitle="Reach out to DASH Physiotherapy & Speech and Hearing Clinic — we're here to help you move, communicate and hear better."
        image={STOCK.audiology}
      />

      <section className="py-24 md:py-32 bg-white" data-testid="contact-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Overline>Contact Details</Overline>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-display text-4xl font-bold text-dash-ink leading-tight">We'd Love to Hear From You</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-4 text-dash-muted leading-relaxed">Call, message on WhatsApp, or fill in the form and our clinic will get back to you shortly.</p>
            </Reveal>

            <div className="mt-8 space-y-4">
              <a href={telLink} data-testid="contact-phone" className="group flex items-center gap-4 rounded-2xl border border-dash-line bg-dash-bg p-5 transition-all hover:border-dash-teal hover:-translate-y-0.5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-dash-teal/10 text-dash-teal"><Phone className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-dash-muted font-accent">Call Now</div>
                  <div className="font-semibold text-dash-ink">{CLINIC.phone}</div>
                </div>
              </a>
              <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="group flex items-center gap-4 rounded-2xl border border-dash-line bg-dash-bg p-5 transition-all hover:border-[#25D366] hover:-translate-y-0.5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#25D366]/10 text-[#25D366]"><MessageCircle className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-dash-muted font-accent">WhatsApp</div>
                  <div className="font-semibold text-dash-ink">{CLINIC.whatsapp}</div>
                </div>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-dash-line bg-dash-bg p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-dash-gold/15 text-dash-gold"><MapPin className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-dash-muted font-accent">Address</div>
                  <div className="font-medium text-dash-ink text-sm mt-1">{CLINIC.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl border border-dash-line bg-dash-bg p-5">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-dash-indigo/10 text-dash-indigo"><Clock className="h-5 w-5" /></span>
                <div>
                  <div className="text-xs uppercase tracking-widest text-dash-muted font-accent">Open</div>
                  <div className="font-medium text-dash-ink text-sm mt-1">Mon–Sat: 9:00 AM–1:30 PM &amp; 4:00 PM–8:00 PM · Sun: 9:00 AM–1:30 PM</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal>
              <ContactForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24 md:pb-32 bg-white" data-testid="map-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] border border-dash-line shadow-[0_20px_40px_rgb(10,37,64,0.06)]">
              <iframe
                title="DASH Clinic Location"
                src={mapSrc}
                data-testid="google-map"
                className="h-[420px] w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>

      <HoursSection />
    </Page>
  );
}
